// pages/Passwords.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button,
  InputGroup, Dropdown, Spinner, Alert
} from 'react-bootstrap';
import ViewPasswordModal from '../components/modals/PasswordModals/ViewPasswordModal';
import PasswordFormModal from '../components/modals/PasswordModals/PasswordFormModal';
import DeletePasswordModal from '../components/modals/PasswordModals/DeletePasswordModal';
import Sidebar from '../components/dashboard/Sidebar';
import ListHeader from '../components/common/ListHeader';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import {
  fetchPasswords,
  viewPassword,
  savePassword,
  deletePassword,
  generateUniquePasswordAlias
} from '../services/password.service';
import { useAuth } from '../contexts/AuthContext';
import { checkPaymentStatus, checkStorageLimits, formatFileSize, formatDateTime, formatItemDate, removeTxtExtension } from '../services/api.service';
import InheritanceDropdownItem from '../components/common/InheritanceDropdownItem';
import PropertiesModal from '../components/common/PropertiesModal';
import { usePaymentStatus } from '../hooks/usePaymentStatus';
import { logEvent } from '../analytics';


import '../styles/pages/passwords.css';
import '../styles/components/modals.css';
import { useRef } from 'react';

const Passwords = () => {
  // State for passwords data
  const [passwords, setPasswords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPasswords, setSelectedPasswords] = useState([]);
  const [sidebarToggled, setSidebarToggled] = useState(false);

  // State for search and sort
  const [searchQuery, setSearchQuery] = useState('');
  const [sortType, setSortType] = useState('none');

  // State for modals
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [multipleDeleteModalOpen, setMultipleDeleteModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddingToInheritance, setIsAddingToInheritance] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);

  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);

  const dropdownRefs = useRef({});
  const cardRefs = useRef({});
  const ignoreNextClick = useRef(false);

  // Get auth context
  const { token, user } = useAuth();
  const { isAccountFrozen, showFrozenAlert } = usePaymentStatus();

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'success'
  });

  // Load passwords
  useEffect(() => {
    loadPasswords();
  }, [token]);

  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ignoreNextClick.current) {
        ignoreNextClick.current = false;
        return;
      }

      const isInsideAnyDropdown = Object.values(dropdownRefs.current).some(
        ref => ref && ref.contains(event.target)
      );

      const isInsideAnyCard = Object.values(cardRefs.current).some(
        ref => ref && ref.contains(event.target)
      );

      if (!isInsideAnyDropdown && !isInsideAnyCard) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadPasswords = async (forceClearCache = false) => {
    setIsLoading(true);
    try {
      // Clear any relevant cached data if force refresh
      if (forceClearCache) {
        // Clear specific password cache
        Object.keys(localStorage).forEach(key => {
          if (key.includes('cache-bucket-passwords')) {
            localStorage.removeItem(key);
          }
        });
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('cache-bucket-passwords')) {
            sessionStorage.removeItem(key);
          }
        });
      }

      // Pass the forceClearCache flag to fetchPasswords
      const data = await fetchPasswords(token, forceClearCache);
      setPasswords(data);
    } catch (error) {
      console.error('Error loading passwords:', error);
      showAlert(`Error loading passwords: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Alert helper
  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });

    // Auto dismiss after 5 seconds
    setTimeout(() => {
      setAlert(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  // Handle view password
  const handleViewPassword = async (password, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      setIsLoading(true);
      const passwordData = await viewPassword(password.name, token);
      setCurrentPassword(passwordData);
      setViewModalOpen(true);
    } catch (error) {
      showAlert(`Error viewing password: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToInheritance = async (password, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setIsAddingToInheritance(true);
    try {
      // Check payment status first
      const paymentStatus = await checkPaymentStatus(token);
      if (paymentStatus.isOverdue) {
             logEvent('inheritance_add_item', {
       item_type: 'password',
       item_alias: password?.name || 'unknown',
       add_result: 'blocked_overdue'
     });
        showAlert('Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.', 'warning');
        return;
      }

      // Import the function from password service
      const { addPasswordToCurrentInheritance } = await import('../services/password.service');

      // Add the password directly to the current inheritance
      await addPasswordToCurrentInheritance(password.name, token);

         logEvent('inheritance_add_item', {
     item_type: 'password',
     item_alias: password?.name || 'unknown',
     add_result: 'success'
   });


      showAlert('Password successfully added to endowment', 'success');
    } catch (error) {
         logEvent('inheritance_add_item', {
     item_type: 'password',
     item_alias: password?.name || 'unknown',
     add_result: 'failure',
     error_message: error?.message || 'unknown'
   });
      showAlert(`Error adding password to endowment: ${error.message}`, 'danger');
    } finally {
      setIsAddingToInheritance(false);
    }
  };

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      // Select all filtered passwords
      filteredPasswords.forEach((password) => {
        if (!selectedPasswords.includes(password.name)) {
          setSelectedPasswords(prev => [...prev, password.name]);
        }
      });
    } else {
      // Deselect all passwords
      setSelectedPasswords([]);
    }
  };

  // Check if all items are selected
  const areAllPasswordsSelected = () => {
    if (filteredPasswords.length === 0) return false;
    return filteredPasswords.every(password => selectedPasswords.includes(password.name));
  };

  // Check if some (but not all) items are selected
  const areSomePasswordsSelected = () => {
    return selectedPasswords.length > 0 && !areAllPasswordsSelected();
  };

  const handleCardClick = (passwordId, e) => {
    if (e) {
      e.stopPropagation();
    }

    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    const timeout = setTimeout(() => {

      // Toggle dropdown when clicking the card
      setOpenDropdownId(prevId => prevId === passwordId ? null : passwordId);
      setClickTimeout(null);
    }, 250);

    setClickTimeout(timeout);
  };

  const handleCardDoubleClick = (password, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Clear the single-click timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Close dropdown if it's open
    setOpenDropdownId(null);

    // Execute the view action (same as clicking "View" in dropdown)
    handleViewPassword(password, e);
  };

  // Handle edit password
  const handleEditPassword = async (password, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      setIsLoading(true);
      const passwordData = await viewPassword(password.name, token);
      setCurrentPassword(passwordData);
      setIsEditing(true);
      setFormModalOpen(true);

      // Check payment status in the background
      checkPermissions();
    } catch (error) {
      showAlert(`Error loading password for editing: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const checkPermissions = async () => {
    try {
      // Check payment status
      const paymentStatus = await checkPaymentStatus(token);
      if (paymentStatus.isOverdue) {
        showAlert('Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.', 'warning');
        setFormModalOpen(false);
        return;
      }

      // Check storage limits
      const storageCheck = await checkStorageLimits(153600, token); // Assuming average password size
      if (!storageCheck.withinLimits) {
        showAlert(`Storage limit exceeded. You have ${formatFileSize(storageCheck.remainingSpace)} remaining.`, 'warning');
        setFormModalOpen(false);
        return;
      }
    } catch (error) {
      showAlert(`Error: ${error.message}`, 'danger');
      setFormModalOpen(false);
    }
  };

  // Handle add password
  const handleAddPassword = async () => {
    // Open modal immediately
    if (isAccountFrozen) {
      showFrozenAlert(showAlert);
      return;
    }
    setCurrentPassword(null);
    setIsEditing(false);
    setFormModalOpen(true);

    // Check permissions in the background
    checkPermissions();
  };

  // Handle delete password
  const handleDeletePassword = (password, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentPassword(password);
    setDeleteModalOpen(true);
  };

  // Handle delete selected passwords
  const handleDeleteSelected = () => {
    if (selectedPasswords.length > 0) {
      setMultipleDeleteModalOpen(true);
    }
  };

  // Confirm delete password
  const confirmDeletePassword = async () => {
    setIsDeleting(true);
    try {
      await deletePassword(currentPassword.name, token);
      showAlert('Password deleted successfully', 'success');
      setDeleteModalOpen(false);
      setCurrentPassword(null);
      loadPasswords();
    } catch (error) {
      showAlert(`Error deleting password: ${error.message}`, 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  // Confirm delete multiple passwords
  const confirmDeleteMultiple = async () => {
    setIsDeleting(true);
    try {
      const deletePromises = selectedPasswords.map(name =>
        deletePassword(name, token)
      );

      await Promise.all(deletePromises);
      showAlert('Selected passwords deleted successfully', 'success');
      setMultipleDeleteModalOpen(false);
      setSelectedPasswords([]);
      loadPasswords();
    } catch (error) {
      showAlert(`Error deleting passwords: ${error.message}`, 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  // Save password
  const handleSavePassword = async (formData, originalName) => {
  setIsSaving(true);
   const action = originalName ? 'update' : 'create';
 const requestedAlias = formData.alias;
 let aliasChanged = false;

  try {
    if (!originalName) {
      // For new passwords, ensure alias is unique
      formData.alias = await generateUniquePasswordAlias(formData.alias, token);
      aliasChanged = formData.alias !== requestedAlias;
    } else {
      // For editing, check if the alias has changed
      const originalAlias = removeTxtExtension(originalName);
      if (formData.alias !== originalAlias) {
        // Alias changed during editing, ensure new alias is unique
        formData.alias = await generateUniquePasswordAlias(formData.alias, token);
        aliasChanged = true;
      }
    }

    await savePassword(formData, originalName, token);
    showAlert('Password saved successfully', 'success');
    setFormModalOpen(false);
    loadPasswords();
       // GA4 success
   logEvent('vault_item_save', {
     item_type: 'password',
     action,
     alias: formData.alias,
     alias_changed: aliasChanged,
     save_result: 'success'
   });
    return true;
  } catch (error) {
    showAlert(`Error saving password: ${error.message}`, 'danger');
       // GA4 failure
   logEvent('vault_item_save', {
     item_type: 'password',
     action,
     alias: formData.alias,
     alias_changed: aliasChanged,
     save_result: 'failure',
     error_message: error?.message || 'unknown'
   });
    throw error;
  } finally {
    setIsSaving(false);
  }
};

  // Handle checkbox selection
  const handleSelectPassword = (name, isSelected, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (isSelected) {
      setSelectedPasswords(prev => [...prev, name]);
    } else {
      setSelectedPasswords(prev => prev.filter(item => item !== name));
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle sort
  const handleSort = (type) => {
    setSortType(type);
    let sortedPasswords = [...passwords];

    switch (type) {
      case 'nameAsc':
        sortedPasswords.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'nameDesc':
        sortedPasswords.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'dateAsc':
        sortedPasswords.sort((a, b) => new Date(a.lastModified) - new Date(b.lastModified));
        break;
      case 'dateDesc':
        sortedPasswords.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
        break;
      default:
        break;
    }

    setPasswords(sortedPasswords);
  };

  // Filter passwords based on search query
  const filteredPasswords = passwords.filter(password =>
    removeTxtExtension(password.name).toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get sort button text
  const getSortButtonText = () => {
    switch (sortType) {
      case 'nameAsc': return 'Name (A-Z)';
      case 'nameDesc': return 'Name (Z-A)';
      case 'dateAsc': return 'Date (Oldest)';
      case 'dateDesc': return 'Date (Newest)';
      default: return 'Sort: None';
    }
  };

  return (
    <div id="page-top">
      <div id="wrapper" className="d-flex">
        <Sidebar toggled={sidebarToggled} setToggled={setSidebarToggled} />

        <div id="content-wrapper" className={`d-flex flex-column ${sidebarToggled ? 'content-toggled' : ''}`}>
          <div id="content">
            <DashboardHeader toggleSidebar={() => setSidebarToggled(!sidebarToggled)} />

            <div className="container-fluid">
              {/* Alert */}
              {alert.show && (
                <Alert
                  variant={alert.variant}
                  onClose={() => setAlert(prev => ({ ...prev, show: false }))}
                  dismissible
                  className="mt-3"
                >
                  {alert.message}
                </Alert>
              )}

              <div className="row">
                <div className="col-lg-12">
                  <div className="card shadow mb-4" style={{ height: 'auto', display: 'flex', flexDirection: 'column', padding: '5px' }}>
                    {/* Use the reusable list header component */}
                    <ListHeader
                      title="Passwords"
                      searchQuery={searchQuery}
                      onSearch={handleSearch}
                      sortType={sortType}
                      onSort={handleSort}
                      onRefresh={() => loadPasswords(true)}
                      isLoading={isLoading}
                      searchPlaceholder="Search passwords..."
                    />

                    {filteredPasswords.length > 0 && (
                      <div className="select-all-container p-2 border-bottom">
                        <div className="d-flex align-items-center">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="selectAllPasswordsCheckbox"
                              checked={areAllPasswordsSelected()}
                              ref={(input) => {
                                if (input) input.indeterminate = areSomePasswordsSelected();
                              }}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              style={{ width: '18px', height: '18px' }}
                            />
                            <label className="form-check-label ms-2" htmlFor="selectAllPasswordsCheckbox">
                              {selectedPasswords.length > 0
                                ? `${selectedPasswords.length} of ${filteredPasswords.length} selected`
                                : `Select all ${filteredPasswords.length} passwords`
                              }
                            </label>
                          </div>

                          
                        </div>
                      </div>
                    )}

                    <div className="card-body header-sort p-0" style={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', padding: '5px' }}>
                      <div id="cardBody" style={{ minHeight: '100%', backgroundColor: '#ffffff', color: '#000000', paddingBottom: '25px' }}>
                        <div style={{ paddingBottom: '25px' }}></div>
                        {isLoading ? (
                          <div className="text-center py-5">
                            <div className="spinner-border" role="status" style={{ color: '#00CC5C' }}>
                              <span className="sr-only">Loading...</span>
                            </div>
                          </div>
                        ) : filteredPasswords.length === 0 ? (
                          <div className="empty-state text-center py-5">
                            <i className="fas fa-lock fa-3x text-black mb-3"></i>
                            <h5 className="text-black">No Passwords Saved Yet</h5>
                            <p className="text-black">Your saved passwords will appear here.</p>
                            <button
                              className="btn btn-sm mt-2"
                              style={{
                                backgroundColor: '#00CC5C',
                                color: '#FFFFFF',
                                padding: '4px 10px',
                                fontSize: '14px',
                                width: 'auto',
                                display: 'inline-block',
                              }}
                              onClick={handleAddPassword}
                              id="addNewPasswordBtn"
                            >
                              <i className="fas fa-plus-circle mr-2" style={{ fontSize: '20px' }}></i>Add New Password
                            </button>
                          </div>
                        ) : (
                          // Password items
                          // Password items
                          filteredPasswords.map(password => (
                            <div
                              key={password.id}
                              className="item d-flex align-items-center card-item-container"
                              id={`card-${password.id}`}
                              ref={el => cardRefs.current[password.id] = el}
                              onClick={(e) => handleCardClick(password.id, e)}
                              onDoubleClick={(e) => handleCardDoubleClick(password, e)}
                              style={{ cursor: 'pointer', position: 'relative' }}
                            >
                              <div className="item-content d-flex align-items-center flex-grow-1">
                                <div className="item-check" style={{ width: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <input
                                    type="checkbox"
                                    id={`checkbox-${password.id}`}
                                    className="form-check-input item-checkbox"
                                    checked={selectedPasswords.includes(password.name)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => handleSelectPassword(password.name, e.target.checked, e)}
                                    style={{ width: '18px', height: '18px', margin: '0' }}
                                  />
                                </div>
                                <div className="item-icon" style={{ width: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <i className="fas fa-key" style={{ color: '#00CC5C', fontSize: '24px' }}></i>
                                </div>
                                <div className="item-name text-truncate" style={{ paddingRight: '10px' }} title={removeTxtExtension(password.name)}>
                                  {removeTxtExtension(password.name)}
                                </div>
                                <div className="item-date text-right ml-auto" style={{ minWidth: '50px' }}>
                                  {formatItemDate(password.lastModified)}
                                </div>
                                <div className="item-size text-right ml-auto" style={{ minWidth: '50px' }}>
                                  {formatFileSize(password.size)}
                                </div>
                                <div className="item-size text-right ml-auto" style={{ minWidth: '20px' }}>
                                  <i className="fas fa-ellipsis-v" style={{ color: '#00CC5C' }}></i>
                                </div>
                              </div>

                              {/* Dropdown menu positioned absolutely */}
                              <div
                                ref={el => dropdownRefs.current[password.id] = el}
                                className={`dropdown-menu dropdown-menu-right ${openDropdownId === password.id ? 'show' : ''}`}
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  right: '0',
                                  zIndex: 1000,
                                  display: openDropdownId === password.id ? 'block' : 'none',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  id={`view-${password.id}`}
                                  onClick={(e) => handleViewPassword(password, e)}
                                >
                                  <i className="fas fa-eye fa-sm fa-fw mr-2 text-black-400"></i>View
                                </a>
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  id={`edit-${password.id}`}
                                  onClick={(e) => handleEditPassword(password, e)}
                                >
                                  <i className="fas fa-pencil-alt fa-sm fa-fw mr-2 text-black-400"></i>Edit
                                </a>
                                {user?.SubscriptionType !== "1" && user?.SubscriptionType !== "3" && (
                                  <InheritanceDropdownItem
                                    onClick={(e) => handleAddToInheritance(password, e)}
                                    isLoading={isAddingToInheritance}
                                    defaultText="Add to Endowment"
                                    loadingText="Adding..."
                                  />
                                )}
                                <div className="dropdown-divider"></div>
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  id={`delete-${password.id}`}
                                  data-action="delete-item"
                                  data-item-name={password.name}
                                  data-item-type="password"
                                  onClick={(e) => handleDeletePassword(password, e)}
                                >
                                  <i className="fas fa-trash-alt fa-sm fa-fw mr-2 text-black-400"></i>Delete
                                </a>
                                <a className="dropdown-item"
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCurrentPassword(password);
                                    setPropertiesModalOpen(true);
                                  }}
                                >
                                  <i className="fas fa-info-circle fa-sm fa-fw mr-2 text-black-400"></i>Properties
                                </a>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Floating Button */}
                  <div id="floatingButtonContainer">
                    {selectedPasswords.length > 0 ? (
                      <button
                        id="deletefloatingButton"
                        className="btn btn-icon"
                        title="Delete"
                        onClick={handleDeleteSelected}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    ) : (
                      <button
                        id="addPasswordButton"
                        className="btn btn-icon-add"
                        title="Add Password"
                        style={{ fontSize: '16px' }}
                        onClick={handleAddPassword}
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ViewPasswordModal
        show={viewModalOpen}
        onHide={() => setViewModalOpen(false)}
        passwordData={currentPassword}
      />

      <PasswordFormModal
        show={formModalOpen}
        onHide={() => setFormModalOpen(false)}
        initialData={isEditing ? currentPassword : null}
        isEditing={isEditing}
        onSave={handleSavePassword}
        isLoading={isSaving}
      />

      <DeletePasswordModal
        show={deleteModalOpen}
        onHide={() => setDeleteModalOpen(false)}
        passwordName={currentPassword?.originalName}
        onConfirm={confirmDeletePassword}
        isSingle={true}
        isLoading={isDeleting}
      />

      <DeletePasswordModal
        show={multipleDeleteModalOpen}
        onHide={() => setMultipleDeleteModalOpen(false)}
        onConfirm={confirmDeleteMultiple}
        isSingle={false}
        isLoading={isDeleting}
      />

      <PropertiesModal
        isOpen={propertiesModalOpen}
        onClose={() => setPropertiesModalOpen(false)}
        itemData={currentPassword}
        itemType="password"
      />
    </div>
  );
};

export default Passwords;